/**
 * Guest photo album — the guest side.
 * Plan: docs/guest-photo-album-plan.md
 *
 * Runs on a phone, on venue wifi, reached from a QR code on a table. Three
 * things drive every decision in here:
 *
 *  1. AN UPLOAD MUST NEVER FAIL AT THE PARTY. Files are sent one at a time,
 *     failures are retried once, and a photo that still won't go is reported
 *     per-file without killing the rest of the batch. The server accepts
 *     uploads well past the free display cap for the same reason — the cap
 *     governs what the couple KEEPS, never what a guest can SEND.
 *  2. Resizing happens HERE, not on the server. `sharp` is a devDependency and
 *     absent in production, and a phone doing its own downscale means we send
 *     ~800KB instead of a 12MB HEIC over hotel wifi. Three renditions go up in
 *     one request: full (2560px), display (1280px), thumb (320px).
 *  3. Nothing about billing is visible here. A guest is never told a photo is
 *     being withheld — the server reports success and the host sees the rest.
 */

(function () {
    'use strict';

    // ---------------------------------------------------------------- config
    const RENDITIONS = {
        full: { max: 2560, quality: 0.85 },
        display: { max: 1280, quality: 0.82 },
        thumb: { max: 320, quality: 0.7 }
    };
    const MAX_SOURCE_BYTES = 25 * 1024 * 1024; // reject absurd files before decoding
    const NAME_KEY = 'qr_album_name';
    const TOKEN_KEY_PREFIX = 'qr_album_token_';

    const slug = (location.pathname.split('/').filter(Boolean).pop() || '').toLowerCase();

    // ------------------------------------------------------------------ i18n
    const STRINGS = {
        en: {
            addPhotos: 'Add your photos', uploading: 'Uploading {done} of {total}…',
            uploaded: 'Thank you! {n} photo{s} added.', someFailed: '{n} photo{s} could not be added. Please try again.',
            empty: 'No photos yet. Be the first — tap “Add your photos”.',
            closedTitle: 'Uploads are closed', closedBody: 'Thanks to everyone who shared photos.',
            opensTitle: 'Photos open on {date}', opensBody: 'Come back on the day to add your photos.',
            demoNote: 'Demo album — the photo you add stays on your phone and is never uploaded. Refresh to clear it.',
            nameTitle: "What's your name?", nameBody: 'So the couple knows who to thank.',
            nameLabel: 'Your name', nameSubmit: 'Continue', cancel: 'Cancel',
            gateTitle: 'Just checking you were there', gateBody: 'This album is for guests only. Enter the name you RSVP’d with.',
            gateLabel: 'Your name', gateSubmit: 'View album',
            gateFailed: 'We couldn’t find that name on the guest list. Try the name you RSVP’d with.',
            notFound: 'This album isn’t available.', tooBig: '{n} file{s} skipped — too large.',
            badType: '{n} file{s} skipped — unsupported format.', by: 'by', footer: 'Photo album by QuikRSVP',
            loading: 'Loading…', pendingNote: 'Your photos were sent to the couple for review.',
            capReached: 'That’s the limit of {n} photos per guest, so the rest were not sent. Thank you for sharing!', slowDown: 'Lots of photos are coming in at once. Wait a few minutes, then add the other {n}.',
            addMedia: 'Add photos & videos', videoUploading: 'Uploading video {done} of {total}: {pct}%', videoProcessing: 'Your video is being prepared and will appear in a minute or two.', videosAdded: '{n} video{s} added.', videoTooLong: '{n} video{s} skipped: longer than {max} seconds.', videoCap: 'That’s the limit of {n} videos per guest, so the rest were not sent.', albumVideoFull: 'This album has reached its video limit, so the rest were not sent.', videosNotAccepted: '{n} video{s} skipped: this album takes photos only.', videoFailed: '{n} video{s} could not be added.'
        },
        he: {
            addPhotos: 'הוסיפו את התמונות שלכם', uploading: 'מעלה {done} מתוך {total}…',
            uploaded: 'תודה! {n} תמונות נוספו.', someFailed: 'לא הצלחנו להוסיף {n} תמונות. נסו שוב.',
            empty: 'עדיין אין תמונות. היו הראשונים — לחצו על "הוסיפו את התמונות שלכם".',
            closedTitle: 'ההעלאות נסגרו', closedBody: 'תודה לכל מי ששיתף תמונות.',
            opensTitle: 'ההעלאות ייפתחו ב-{date}', opensBody: 'חזרו ביום האירוע כדי להוסיף את התמונות שלכם.',
            demoNote: 'אלבום הדגמה — התמונה שתוסיפו נשארת על המכשיר שלכם ולא נשלחת לשום מקום. רעננו כדי לנקות.',
            nameTitle: 'מה השם שלכם?', nameBody: 'כדי שבני הזוג ידעו למי להודות.',
            nameLabel: 'השם שלכם', nameSubmit: 'המשך', cancel: 'ביטול',
            gateTitle: 'רק מוודאים שהייתם שם', gateBody: 'האלבום הזה מיועד לאורחים בלבד. הזינו את השם שאיתו אישרתם הגעה.',
            gateLabel: 'השם שלכם', gateSubmit: 'צפייה באלבום',
            gateFailed: 'לא מצאנו את השם ברשימת האורחים. נסו את השם שאיתו אישרתם הגעה.',
            notFound: 'האלבום הזה אינו זמין.', tooBig: '{n} קבצים דולגו — גדולים מדי.',
            badType: '{n} קבצים דולגו — פורמט לא נתמך.', by: 'מאת', footer: 'אלבום תמונות של QuikRSVP',
            loading: 'טוען…', pendingNote: 'התמונות שלכם נשלחו לבני הזוג לאישור.',
            capReached: 'זו המגבלה של {n} תמונות לכל אורח, ולכן השאר לא נשלחו. תודה ששיתפתם!', slowDown: 'הרבה תמונות נכנסות עכשיו בבת אחת. חכו כמה דקות ואז הוסיפו את {n} הנותרות.',
            addMedia: 'הוסיפו תמונות וסרטונים', videoUploading: 'מעלה סרטון {done} מתוך {total}: {pct}%', videoProcessing: 'הסרטון שלכם בהכנה ויופיע בעוד דקה או שתיים.', videosAdded: '{n} סרטונים נוספו.', videoTooLong: '{n} סרטונים דולגו: ארוכים מ-{max} שניות.', videoCap: 'זו המגבלה של {n} סרטונים לכל אורח, ולכן השאר לא נשלחו.', albumVideoFull: 'האלבום הגיע למגבלת הסרטונים שלו, ולכן השאר לא נשלחו.', videosNotAccepted: '{n} סרטונים דולגו: האלבום הזה מקבל תמונות בלבד.', videoFailed: 'לא הצלחנו להוסיף {n} סרטונים.'
        },
        es: {
            addPhotos: 'Añade tus fotos', uploading: 'Subiendo {done} de {total}…',
            uploaded: '¡Gracias! {n} foto{s} añadida{s}.', someFailed: 'No se pudieron añadir {n} foto{s}. Inténtalo de nuevo.',
            empty: 'Aún no hay fotos. Sé el primero: toca «Añade tus fotos».',
            closedTitle: 'Las subidas están cerradas', closedBody: 'Gracias a todos los que compartieron fotos.',
            opensTitle: 'Podrás subir fotos desde el {date}', opensBody: 'Vuelve ese día para añadir tus fotos.',
            demoNote: 'Álbum de demostración: la foto que añadas se queda en tu móvil y no se sube a ningún sitio. Recarga para borrarla.',
            nameTitle: '¿Cómo te llamas?', nameBody: 'Para que la pareja sepa a quién dar las gracias.',
            nameLabel: 'Tu nombre', nameSubmit: 'Continuar', cancel: 'Cancelar',
            gateTitle: 'Solo comprobamos que estuviste', gateBody: 'Este álbum es solo para invitados. Escribe el nombre con el que confirmaste.',
            gateLabel: 'Tu nombre', gateSubmit: 'Ver álbum',
            gateFailed: 'No encontramos ese nombre en la lista. Prueba con el nombre con el que confirmaste.',
            notFound: 'Este álbum no está disponible.', tooBig: '{n} archivo{s} omitido{s}: demasiado grande{s}.',
            badType: '{n} archivo{s} omitido{s}: formato no admitido.', by: 'por', footer: 'Álbum de fotos de QuikRSVP',
            loading: 'Cargando…', pendingNote: 'Tus fotos se enviaron a la pareja para su revisión.',
            capReached: 'Ese es el límite de {n} fotos por invitado, así que el resto no se envió. ¡Gracias por compartir!', slowDown: 'Están llegando muchas fotos a la vez. Espera unos minutos y añade las {n} restantes.',
            addMedia: 'Añade fotos y vídeos', videoUploading: 'Subiendo vídeo {done} de {total}: {pct}%', videoProcessing: 'Tu vídeo se está preparando y aparecerá en uno o dos minutos.', videosAdded: '{n} vídeo{s} añadido{s}.', videoTooLong: '{n} vídeo{s} omitido{s}: dura más de {max} segundos.', videoCap: 'Ese es el límite de {n} vídeos por invitado, así que el resto no se envió.', albumVideoFull: 'Este álbum llegó a su límite de vídeo, así que el resto no se envió.', videosNotAccepted: '{n} vídeo{s} omitido{s}: este álbum solo admite fotos.', videoFailed: 'No se pudieron añadir {n} vídeo{s}.'
        },
        fr: {
            addPhotos: 'Ajoutez vos photos', uploading: 'Envoi de {done} sur {total}…',
            uploaded: 'Merci ! {n} photo{s} ajoutée{s}.', someFailed: '{n} photo{s} n’ont pas pu être ajoutées. Réessayez.',
            empty: 'Pas encore de photos. Soyez le premier : appuyez sur « Ajoutez vos photos ».',
            closedTitle: 'Les envois sont clos', closedBody: 'Merci à tous ceux qui ont partagé des photos.',
            opensTitle: 'Les envois ouvrent le {date}', opensBody: 'Revenez ce jour-là pour ajouter vos photos.',
            demoNote: "Album de démonstration : la photo que vous ajoutez reste sur votre téléphone et n'est jamais envoyée. Rechargez pour l'effacer.",
            nameTitle: 'Quel est votre nom ?', nameBody: 'Pour que les mariés sachent qui remercier.',
            nameLabel: 'Votre nom', nameSubmit: 'Continuer', cancel: 'Annuler',
            gateTitle: 'On vérifie juste que vous y étiez', gateBody: 'Cet album est réservé aux invités. Saisissez le nom utilisé pour votre réponse.',
            gateLabel: 'Votre nom', gateSubmit: 'Voir l’album',
            gateFailed: 'Nous n’avons pas trouvé ce nom sur la liste. Essayez le nom utilisé pour votre réponse.',
            notFound: 'Cet album n’est pas disponible.', tooBig: '{n} fichier{s} ignoré{s} — trop volumineux.',
            badType: '{n} fichier{s} ignoré{s} — format non pris en charge.', by: 'par', footer: 'Album photo de QuikRSVP',
            loading: 'Chargement…', pendingNote: 'Vos photos ont été envoyées aux mariés pour validation.',
            capReached: 'C’est la limite de {n} photos par invité, les autres n’ont donc pas été envoyées. Merci pour le partage !', slowDown: 'Beaucoup de photos arrivent en même temps. Attendez quelques minutes, puis ajoutez les {n} restantes.',
            addMedia: 'Ajoutez photos et vidéos', videoUploading: 'Envoi de la vidéo {done} sur {total} : {pct} %', videoProcessing: 'Votre vidéo est en préparation et apparaîtra d’ici une ou deux minutes.', videosAdded: '{n} vidéo{s} ajoutée{s}.', videoTooLong: '{n} vidéo{s} ignorée{s} : plus de {max} secondes.', videoCap: 'C’est la limite de {n} vidéos par invité, les autres n’ont donc pas été envoyées.', albumVideoFull: 'Cet album a atteint sa limite de vidéos, les autres n’ont donc pas été envoyées.', videosNotAccepted: '{n} vidéo{s} ignorée{s} : cet album accepte seulement les photos.', videoFailed: '{n} vidéo{s} n’ont pas pu être ajoutées.'
        },
        de: {
            addPhotos: 'Fotos hinzufügen', uploading: 'Lade {done} von {total} hoch…',
            uploaded: 'Danke! {n} Foto{s} hinzugefügt.', someFailed: '{n} Foto{s} konnten nicht hinzugefügt werden. Bitte erneut versuchen.',
            empty: 'Noch keine Fotos. Sei der Erste – tippe auf „Fotos hinzufügen“.',
            closedTitle: 'Uploads sind geschlossen', closedBody: 'Danke an alle, die Fotos geteilt haben.',
            opensTitle: 'Uploads öffnen am {date}', opensBody: 'Komm an dem Tag wieder, um deine Fotos hinzuzufügen.',
            demoNote: 'Demo-Album — das Foto, das ihr hinzufügt, bleibt auf eurem Handy und wird nie hochgeladen. Neu laden zum Zurücksetzen.',
            nameTitle: 'Wie heißt du?', nameBody: 'Damit das Paar weiß, wem es danken kann.',
            nameLabel: 'Dein Name', nameSubmit: 'Weiter', cancel: 'Abbrechen',
            gateTitle: 'Nur kurz geprüft', gateBody: 'Dieses Album ist nur für Gäste. Gib den Namen ein, mit dem du zugesagt hast.',
            gateLabel: 'Dein Name', gateSubmit: 'Album ansehen',
            gateFailed: 'Diesen Namen haben wir nicht auf der Gästeliste gefunden. Versuche den Namen deiner Zusage.',
            notFound: 'Dieses Album ist nicht verfügbar.', tooBig: '{n} Datei{s} übersprungen – zu groß.',
            badType: '{n} Datei{s} übersprungen – Format nicht unterstützt.', by: 'von', footer: 'Fotoalbum von QuikRSVP',
            loading: 'Lädt…', pendingNote: 'Deine Fotos wurden dem Paar zur Prüfung geschickt.',
            capReached: 'Das ist das Limit von {n} Fotos pro Gast, deshalb wurden die übrigen nicht gesendet. Danke fürs Teilen!', slowDown: 'Gerade kommen sehr viele Fotos gleichzeitig an. Warte ein paar Minuten und füge dann die übrigen {n} hinzu.',
            addMedia: 'Fotos & Videos hinzufügen', videoUploading: 'Lade Video {done} von {total} hoch: {pct} %', videoProcessing: 'Dein Video wird vorbereitet und erscheint in ein bis zwei Minuten.', videosAdded: '{n} Video{s} hinzugefügt.', videoTooLong: '{n} Video{s} übersprungen: länger als {max} Sekunden.', videoCap: 'Das ist das Limit von {n} Videos pro Gast, deshalb wurden die übrigen nicht gesendet.', albumVideoFull: 'Dieses Album hat sein Video-Limit erreicht, deshalb wurden die übrigen nicht gesendet.', videosNotAccepted: '{n} Video{s} übersprungen: dieses Album nimmt nur Fotos.', videoFailed: '{n} Video{s} konnten nicht hinzugefügt werden.'
        },
        pt: {
            addPhotos: 'Adicione as suas fotos', uploading: 'A enviar {done} de {total}…',
            uploaded: 'Obrigado! {n} foto{s} adicionada{s}.', someFailed: 'Não foi possível adicionar {n} foto{s}. Tente novamente.',
            empty: 'Ainda não há fotos. Seja o primeiro — toque em «Adicione as suas fotos».',
            closedTitle: 'Os envios estão encerrados', closedBody: 'Obrigado a todos os que partilharam fotos.',
            opensTitle: 'Os envios abrem em {date}', opensBody: 'Volte nesse dia para adicionar suas fotos.',
            demoNote: 'Álbum de demonstração — a foto que acrescentar fica no seu telemóvel e nunca é enviada. Recarregue para limpar.',
            nameTitle: 'Como se chama?', nameBody: 'Para que o casal saiba a quem agradecer.',
            nameLabel: 'O seu nome', nameSubmit: 'Continuar', cancel: 'Cancelar',
            gateTitle: 'Só a confirmar que esteve lá', gateBody: 'Este álbum é só para convidados. Escreva o nome com que confirmou presença.',
            gateLabel: 'O seu nome', gateSubmit: 'Ver álbum',
            gateFailed: 'Não encontrámos esse nome na lista. Tente o nome com que confirmou presença.',
            notFound: 'Este álbum não está disponível.', tooBig: '{n} ficheiro{s} ignorado{s} — demasiado grande{s}.',
            badType: '{n} ficheiro{s} ignorado{s} — formato não suportado.', by: 'por', footer: 'Álbum de fotos de QuikRSVP',
            loading: 'A carregar…', pendingNote: 'As suas fotos foram enviadas ao casal para revisão.',
            capReached: 'Este é o limite de {n} fotos por convidado, por isso as restantes não foram enviadas. Obrigado por partilhar!', slowDown: 'Estão a chegar muitas fotos ao mesmo tempo. Aguarde alguns minutos e depois adicione as {n} restantes.',
            addMedia: 'Adicione fotos e vídeos', videoUploading: 'A enviar o vídeo {done} de {total}: {pct}%', videoProcessing: 'O seu vídeo está a ser preparado e aparece dentro de um ou dois minutos.', videosAdded: '{n} vídeo{s} adicionado{s}.', videoTooLong: '{n} vídeo{s} ignorado{s}: mais de {max} segundos.', videoCap: 'Este é o limite de {n} vídeos por convidado, por isso os restantes não foram enviados.', albumVideoFull: 'Este álbum atingiu o limite de vídeos, por isso os restantes não foram enviados.', videosNotAccepted: '{n} vídeo{s} ignorado{s}: este álbum aceita apenas fotos.', videoFailed: 'Não foi possível adicionar {n} vídeo{s}.'
        },
        ar: {
            addPhotos: 'أضف صورك', uploading: 'جاري رفع {done} من {total}…',
            uploaded: 'شكرًا! تمت إضافة {n} صورة.', someFailed: 'تعذّرت إضافة {n} صورة. حاول مرة أخرى.',
            empty: 'لا توجد صور بعد. كن الأول — اضغط على «أضف صورك».',
            closedTitle: 'تم إغلاق الرفع', closedBody: 'شكرًا لكل من شارك صورًا.',
            opensTitle: 'يُفتح رفع الصور في {date}', opensBody: 'عودوا في ذلك اليوم لإضافة صوركم.',
            demoNote: 'ألبوم تجريبي — الصورة التي تضيفها تبقى على هاتفك ولا تُرفع أبدًا. أعد التحميل لمسحها.',
            nameTitle: 'ما اسمك؟', nameBody: 'ليعرف العروسان لمن يوجّهان الشكر.',
            nameLabel: 'اسمك', nameSubmit: 'متابعة', cancel: 'إلغاء',
            gateTitle: 'نتأكد فقط أنك كنت هناك', gateBody: 'هذا الألبوم للضيوف فقط. أدخل الاسم الذي أكدت به حضورك.',
            gateLabel: 'اسمك', gateSubmit: 'عرض الألبوم',
            gateFailed: 'لم نجد هذا الاسم في قائمة الضيوف. جرّب الاسم الذي أكدت به حضورك.',
            notFound: 'هذا الألبوم غير متاح.', tooBig: 'تم تخطي {n} ملف — كبير جدًا.',
            badType: 'تم تخطي {n} ملف — صيغة غير مدعومة.', by: 'بواسطة', footer: 'ألبوم صور QuikRSVP',
            loading: 'جاري التحميل…', pendingNote: 'أُرسلت صورك إلى العروسين للمراجعة.',
            capReached: 'هذا هو الحد الأقصى وهو {n} صورة لكل ضيف، لذلك لم تُرسل بقية الصور. شكرًا على المشاركة!', slowDown: 'تصل صور كثيرة في الوقت نفسه. انتظر بضع دقائق ثم أضف الصور المتبقية ({n}).',
            addMedia: 'أضف صورًا وفيديوهات', videoUploading: 'جاري رفع الفيديو {done} من {total}: {pct}%', videoProcessing: 'يجري تجهيز الفيديو وسيظهر خلال دقيقة أو دقيقتين.', videosAdded: 'تمت إضافة {n} فيديو.', videoTooLong: 'تم تخطي {n} فيديو: أطول من {max} ثانية.', videoCap: 'هذا هو الحد الأقصى وهو {n} فيديو لكل ضيف، لذلك لم تُرسل البقية.', albumVideoFull: 'بلغ هذا الألبوم حد الفيديو، لذلك لم تُرسل البقية.', videosNotAccepted: 'تم تخطي {n} فيديو: هذا الألبوم يقبل الصور فقط.', videoFailed: 'تعذّرت إضافة {n} فيديو.'
        }
    };
    const RTL = new Set(['he', 'ar']);

    function pickLang() {
        const q = new URLSearchParams(location.search).get('lang');
        if (q && STRINGS[q]) return q;
        const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
        return STRINGS[nav] ? nav : 'en';
    }
    const LANG = pickLang();
    const T = STRINGS[LANG];
    function t(key, vars) {
        let s = T[key] || STRINGS.en[key] || key;
        if (vars) {
            Object.keys(vars).forEach((k) => { s = s.split('{' + k + '}').join(vars[k]); });
            // Crude English/Romance plural marker: {s} becomes "s" for n != 1.
            s = s.split('{s}').join(Number(vars.n) === 1 ? '' : 's');
        }
        return s;
    }

    // ------------------------------------------------------------------ dom
    const $ = (id) => document.getElementById(id);
    const els = {
        title: $('albumTitle'), subtitle: $('albumSubtitle'),
        gate: $('gate'), gateTitle: $('gateTitle'), gateBody: $('gateBody'),
        gateLabel: $('gateLabel'), gateName: $('gateName'), gateSubmit: $('gateSubmit'), gateStatus: $('gateStatus'),
        body: $('albumBody'), grid: $('grid'), status: $('status'), empty: $('emptyState'),
        closed: $('closedNotice'), closedTitle: $('closedTitle'), closedBody: $('closedBody'),
        dock: $('dock'), uploadBtn: $('uploadBtn'), file: $('fileInput'),
        namePrompt: $('namePrompt'), nameTitle: $('nameTitle'), nameBody: $('nameBody'),
        nameLabel: $('nameLabel'), guestName: $('guestName'), nameSubmit: $('nameSubmit'), nameCancel: $('nameCancel'),
        lightbox: $('lightbox'), lightboxImg: $('lightboxImg'), lightboxMeta: $('lightboxMeta'), lightboxClose: $('lightboxClose'),
        lightboxPrev: $('lightboxPrev'), lightboxNext: $('lightboxNext'), lightboxCount: $('lightboxCount'),
        footerNote: $('footerNote')
    };
    // The viewer's video player (Stream's own iframe). Built here rather than
    // in album.html so the markup stays the photo page it has always been.
    els.lightboxVideo = document.createElement('iframe');
    els.lightboxVideo.className = 'lb-video hidden';
    els.lightboxVideo.setAttribute('allow', 'accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture');
    els.lightboxVideo.setAttribute('allowfullscreen', '');
    els.lightboxImg.insertAdjacentElement('afterend', els.lightboxVideo);
    const IMAGE_ACCEPT = els.file.getAttribute('accept') || 'image/*';

    let album = null;
    let busy = false;

    function applyStaticStrings() {
        document.documentElement.lang = LANG;
        if (RTL.has(LANG)) document.documentElement.dir = 'rtl';
        els.uploadBtn.textContent = t('addPhotos');
        els.gateTitle.textContent = t('gateTitle');
        els.gateBody.textContent = t('gateBody');
        els.gateLabel.textContent = t('gateLabel');
        els.gateSubmit.textContent = t('gateSubmit');
        els.nameTitle.textContent = t('nameTitle');
        els.nameBody.textContent = t('nameBody');
        els.nameLabel.textContent = t('nameLabel');
        els.nameSubmit.textContent = t('nameSubmit');
        els.nameCancel.textContent = t('cancel');
        els.closedTitle.textContent = t('closedTitle');
        els.closedBody.textContent = t('closedBody');
        els.empty.textContent = t('empty');
        els.footerNote.textContent = t('footer');
        els.status.textContent = t('loading');
    }

    // ---------------------------------------------------------------- token
    const tokenKey = () => TOKEN_KEY_PREFIX + slug;
    function getToken() { try { return localStorage.getItem(tokenKey()) || ''; } catch { return ''; } }
    function setToken(v) { try { localStorage.setItem(tokenKey(), v); } catch { /* private mode */ } }
    function getName() { try { return localStorage.getItem(NAME_KEY) || ''; } catch { return ''; } }
    function setName(v) { try { localStorage.setItem(NAME_KEY, v); } catch { /* private mode */ } }

    // The host's own link from the dashboard ("Add your own photos") carries
    // ?owner=<token>: kept for this album (in memory too, for private mode)
    // and taken out of the address bar, so a host who copies the link for
    // their guests does not hand them the host's upload rights.
    const ownerKey = () => 'qr_album_owner_' + slug;
    let ownerToken = '';
    try { ownerToken = localStorage.getItem(ownerKey()) || ''; } catch { /* private mode */ }
    (function takeOwnerToken() {
        const params = new URLSearchParams(location.search);
        const fromUrl = params.get('owner');
        if (!fromUrl) return;
        ownerToken = fromUrl;
        try { localStorage.setItem(ownerKey(), fromUrl); } catch { /* private mode */ }
        params.delete('owner');
        const rest = params.toString();
        try { history.replaceState(null, '', location.pathname + (rest ? '?' + rest : '') + location.hash); } catch { /* ignore */ }
    })();

    // ----------------------------------------------------------------- api
    async function api(path, options) {
        const opts = Object.assign({ headers: {} }, options || {});
        opts.headers = Object.assign({ 'Content-Type': 'application/json' }, opts.headers);
        const token = getToken();
        if (token) opts.headers['X-Album-Token'] = token;
        if (ownerToken) opts.headers['X-Album-Owner'] = ownerToken;
        const res = await fetch('/api/albums/public/' + encodeURIComponent(slug) + path, opts);
        let data = null;
        try { data = await res.json(); } catch { data = null; }
        if (!res.ok) {
            const err = new Error((data && data.error) || 'Request failed');
            err.status = res.status;
            if (data && data.code) err.code = data.code;
            if (data && data.limit) err.limit = data.limit;
            throw err;
        }
        return data;
    }

    // ------------------------------------------------------------- rendering
    // The rendered set, so the lightbox can step through exactly what is on
    // screen rather than re-deriving it.
    let currentPhotos = [];

    // ------------------------------------------------------ justified layout
    // Photos are laid out in rows that each fill the width exactly at a shared
    // height, so every picture keeps its real shape. A square grid cropped the
    // top and bottom off every portrait photo, which at a wedding is most of
    // the dance-floor shots.

    const DEFAULT_ASPECT = 1;   // photos uploaded before dimensions were stored
    const MIN_ASPECT = 0.4;     // clamp so one freak panorama cannot flatten a row
    const MAX_ASPECT = 3.5;

    function aspectOf(p) {
        if (!p.width || !p.height) return DEFAULT_ASPECT;
        const a = p.width / p.height;
        if (!isFinite(a) || a <= 0) return DEFAULT_ASPECT;
        return Math.min(MAX_ASPECT, Math.max(MIN_ASPECT, a));
    }

    /** Row height to aim for, before each row is scaled to fill the width. */
    function targetRowHeight(width) {
        if (width < 500) return 130;
        if (width < 900) return 190;
        return 250;
    }

    function gapSize(width) {
        if (width >= 1024) return 10;
        if (width >= 620) return 8;
        return 6;
    }

    /**
     * Greedy justified rows: add photos until the row is at least as wide as
     * the container at the target height, then scale that row to fit exactly.
     */
    function buildRows(photos, containerWidth, target, gap) {
        const rows = [];
        let row = [];
        let aspectSum = 0;
        for (const p of photos) {
            const a = aspectOf(p);
            row.push({ photo: p, aspect: a });
            aspectSum += a;
            const projected = aspectSum * target + gap * (row.length - 1);
            if (projected >= containerWidth) {
                const avail = containerWidth - gap * (row.length - 1);
                rows.push({ items: row, height: avail / aspectSum });
                row = [];
                aspectSum = 0;
            }
        }
        if (row.length) {
            const avail = containerWidth - gap * (row.length - 1);
            // A trailing row is NOT stretched — one leftover photo blown up to
            // full width would dwarf everything above it.
            rows.push({ items: row, height: Math.min(target, avail / aspectSum) });
        }
        return rows;
    }

    function render(photos) {
        currentPhotos = photos || [];
        els.grid.innerHTML = '';
        if (!photos || !photos.length) {
            els.empty.classList.remove('hidden');
            return;
        }
        els.empty.classList.add('hidden');
        layoutGrid();
    }

    function layoutGrid() {
        const photos = currentPhotos;
        if (!photos.length) return;

        const containerWidth = els.grid.clientWidth;
        if (!containerWidth) {
            // Not measurable yet (hidden tab, first paint) — show the square
            // fallback so the album is never blank, and re-run when it is.
            els.grid.classList.add('grid-fallback');
            return;
        }
        els.grid.classList.remove('grid-fallback');

        const gap = gapSize(containerWidth);
        const rows = buildRows(photos, containerWidth, targetRowHeight(containerWidth), gap);

        const frag = document.createDocumentFragment();
        let index = 0;
        for (const row of rows) {
            const rowEl = document.createElement('div');
            rowEl.className = 'jrow';
            for (const item of row.items) {
                const p = item.photo;
                const idx = index++;
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.style.width = Math.floor(item.aspect * row.height) + 'px';
                btn.style.height = Math.floor(row.height) + 'px';

                const img = document.createElement('img');
                img.src = p.thumbUrl;
                img.loading = 'lazy';
                img.decoding = 'async';
                img.alt = p.uploaderName ? t('by') + ' ' + p.uploaderName : '';
                btn.appendChild(img);

                if (p.type === 'video') {
                    const badge = document.createElement('span');
                    badge.className = 'vbadge';
                    badge.textContent = '▶' + (p.durationSec ? ' ' + fmtDuration(p.durationSec) : '');
                    btn.appendChild(badge);
                }
                if (p.uploaderName) {
                    const who = document.createElement('span');
                    who.className = 'who';
                    who.textContent = p.uploaderName; // textContent, never innerHTML — guest-supplied
                    btn.appendChild(who);
                }
                btn.addEventListener('click', () => openLightbox(p, idx));
                rowEl.appendChild(btn);
            }
            frag.appendChild(rowEl);
        }
        els.grid.innerHTML = '';
        els.grid.appendChild(frag);
    }

    // Re-flow on width changes. ResizeObserver catches the container changing
    // for reasons other than the window (rotation, keyboard, zoom); the resize
    // listener is the fallback where it is unavailable.
    let layoutTimer = null;
    let lastLayoutWidth = 0;
    function scheduleLayout() {
        clearTimeout(layoutTimer);
        layoutTimer = setTimeout(() => {
            const w = els.grid.clientWidth;
            if (w && w !== lastLayoutWidth) {
                lastLayoutWidth = w;
                layoutGrid();
            }
        }, 120);
    }
    window.addEventListener('resize', scheduleLayout);
    window.addEventListener('orientationchange', scheduleLayout);
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(scheduleLayout).observe(els.grid);
    }

    // ------------------------------------------------------------- lightbox
    // Browsing IS the main action on this page, so the viewer moves photo to
    // photo: arrow keys, swipe, and tap targets. Without it the loop was
    // open-look-close-tap-open, once per photo.
    let lbPhotos = [];
    let lbIndex = -1;
    let lbReturnFocus = null;

    const isRtl = () => document.documentElement.dir === 'rtl';

    function openLightbox(p, index) {
        lbPhotos = currentPhotos;
        lbIndex = typeof index === 'number' ? index : lbPhotos.indexOf(p);
        lbReturnFocus = document.activeElement;
        showLightboxAt(lbIndex);
        els.lightbox.classList.add('open');
        // Stop the grid scrolling underneath the viewer on iOS.
        document.body.style.overflow = 'hidden';
        els.lightboxClose.focus();
    }

    function showLightboxAt(i) {
        const p = lbPhotos[i];
        if (!p) return;
        lbIndex = i;
        if (p.type === 'video' && p.iframeUrl) {
            els.lightboxImg.classList.add('hidden');
            els.lightboxImg.removeAttribute('src');
            sizeVideo(p);
            if (els.lightboxVideo.src !== p.iframeUrl) els.lightboxVideo.src = p.iframeUrl;
            els.lightboxVideo.classList.remove('hidden');
        } else {
            stopVideo();
            els.lightboxImg.classList.remove('hidden');
        }
        if (p.type !== 'video') els.lightboxImg.src = p.displayUrl;
        els.lightboxImg.alt = p.uploaderName ? t('by') + ' ' + p.uploaderName : '';
        els.lightboxMeta.textContent = p.uploaderName ? t('by') + ' ' + p.uploaderName : '';
        if (els.lightboxCount) els.lightboxCount.textContent = `${i + 1} / ${lbPhotos.length}`;
        els.lightboxPrev.disabled = i <= 0;
        els.lightboxNext.disabled = i >= lbPhotos.length - 1;
        // Warm the neighbours so stepping through does not flash.
        [lbPhotos[i - 1], lbPhotos[i + 1]].forEach((n) => {
            if (n) { const img = new Image(); img.src = n.displayUrl; }
        });
    }

    /** Fit the player to the clip's own shape (phones film upright) and the screen. */
    function sizeVideo(p) {
        const r = (p.width && p.height) ? p.width / p.height : 16 / 9;
        const maxW = Math.min(window.innerWidth - 32, 960);
        const maxH = window.innerHeight * 0.82;
        let w = maxW, h = maxW / r;
        if (h > maxH) { h = maxH; w = maxH * r; }
        els.lightboxVideo.style.width = Math.floor(w) + 'px';
        els.lightboxVideo.style.height = Math.floor(h) + 'px';
    }
    function stopVideo() {
        if (els.lightboxVideo.getAttribute('src')) els.lightboxVideo.removeAttribute('src');
        els.lightboxVideo.classList.add('hidden');
    }
    function fmtDuration(sec) {
        const s = Math.round(sec);
        return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
    }

    function stepLightbox(delta) {
        const next = lbIndex + delta;
        if (next < 0 || next >= lbPhotos.length) return;
        showLightboxAt(next);
    }

    function closeLightbox() {
        els.lightbox.classList.remove('open');
        document.body.style.overflow = '';
        els.lightboxImg.src = '';
        stopVideo();
        if (lbReturnFocus && lbReturnFocus.focus) lbReturnFocus.focus();
        lbReturnFocus = null;
    }

    els.lightboxClose.addEventListener('click', closeLightbox);
    els.lightboxPrev.addEventListener('click', () => stepLightbox(-1));
    els.lightboxNext.addEventListener('click', () => stepLightbox(1));
    els.lightbox.addEventListener('click', (e) => {
        if (e.target === els.lightbox) closeLightbox();
    });

    document.addEventListener('keydown', (e) => {
        if (!els.lightbox.classList.contains('open')) return;
        if (e.key === 'Escape') { closeLightbox(); return; }
        // In RTL the grid runs right-to-left, so the arrow that means "onward"
        // is the left one. Mirror it rather than fighting the reading order.
        const forward = isRtl() ? 'ArrowLeft' : 'ArrowRight';
        const back = isRtl() ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === forward) { e.preventDefault(); stepLightbox(1); }
        else if (e.key === back) { e.preventDefault(); stepLightbox(-1); }
        else if (e.key === 'Tab') {
            // Keep focus inside the viewer while it is open.
            const focusables = [els.lightboxClose, els.lightboxPrev, els.lightboxNext]
                .filter((el) => el && !el.disabled);
            const i = focusables.indexOf(document.activeElement);
            const nextIndex = e.shiftKey
                ? (i <= 0 ? focusables.length - 1 : i - 1)
                : (i === focusables.length - 1 ? 0 : i + 1);
            e.preventDefault();
            focusables[nextIndex]?.focus();
        }
    });

    // Swipe. Mirrored in RTL for the same reason as the arrow keys.
    let touchX = null;
    let touchY = null;
    els.lightbox.addEventListener('touchstart', (e) => {
        touchX = e.changedTouches[0].clientX;
        touchY = e.changedTouches[0].clientY;
    }, { passive: true });
    els.lightbox.addEventListener('touchend', (e) => {
        if (touchX === null) return;
        const dx = e.changedTouches[0].clientX - touchX;
        const dy = e.changedTouches[0].clientY - touchY;
        touchX = touchY = null;
        // Ignore mostly-vertical drags so a scroll gesture is not read as a swipe.
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
        const forward = isRtl() ? dx > 0 : dx < 0;
        stepLightbox(forward ? 1 : -1);
    }, { passive: true });

    function setStatus(msg, kind) {
        els.status.textContent = msg || '';
        els.status.className = 'status' + (kind ? ' ' + kind : '');
    }

    // --------------------------------------------------------------- loading
    async function load() {
        try {
            const data = await api('');
            album = data.album;
            els.title.textContent = album.title || '';
            document.title = album.title || 'Photo Album';

            if (data.locked) {
                els.gate.classList.remove('hidden');
                els.body.classList.add('hidden');
                els.dock.classList.add('hidden');
                setStatus('');
                return;
            }

            els.gate.classList.add('hidden');
            els.body.classList.remove('hidden');
            setStatus('');
            render(data.photos);

            // The marketing demo keeps its upload dock even though uploads are
            // closed: adding a photo is simulated on the visitor's own device
            // (see uploadAll). Its uploads are closed precisely BECAUSE we
            // advertise it — a public write endpoint on a page we link from
            // marketing is an open invitation, and album-uploads/** is
            // public-read, so a deleted-later file has still been distributed.
            isDemo = album.isDemo === true;
            const takesVideo = album.acceptsVideo === true && !isDemo;
            els.file.setAttribute('accept', takesVideo ? IMAGE_ACCEPT + ',video/*' : IMAGE_ACCEPT);
            els.uploadBtn.textContent = takesVideo ? t('addMedia') : t('addPhotos');
            if (album.uploadsOpen || isDemo) {
                els.dock.classList.remove('hidden');
                els.closed.classList.add('hidden');
                if (isDemo) setStatus(t('demoNote'));
            } else {
                els.dock.classList.add('hidden');
                els.closed.classList.remove('hidden');
                // Not open YET (the host chose the event day) reads differently
                // from closed for good: nobody should be thanked for photos
                // before the party.
                if (album.opensAt) {
                    let day = '';
                    // The event's own day (opensOn), not the host's midnight
                    // in this guest's zone, which is the day before west of
                    // the host. opensAt alone is the fallback for an older API.
                    const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(album.opensOn || '');
                    try {
                        day = p
                            ? new Date(Date.UTC(+p[1], +p[2] - 1, +p[3])).toLocaleDateString(LANG, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
                            : new Date(album.opensAt).toLocaleDateString(LANG, { weekday: 'long', month: 'long', day: 'numeric' });
                    } catch { day = ''; }
                    els.closedTitle.textContent = t('opensTitle', { date: day });
                    els.closedBody.textContent = t('opensBody');
                } else {
                    els.closedTitle.textContent = t('closedTitle');
                    els.closedBody.textContent = t('closedBody');
                }
            }
        } catch (err) {
            els.body.classList.remove('hidden');
            setStatus(err.status === 404 ? t('notFound') : (err.message || t('notFound')), 'error');
            els.dock.classList.add('hidden');
        }
    }

    // ------------------------------------------------------------- the gate
    els.gateSubmit.addEventListener('click', submitGate);
    els.gateName.addEventListener('keydown', (e) => { if (e.key === 'Enter') submitGate(); });

    async function submitGate() {
        const name = els.gateName.value.trim();
        if (name.length < 2) return;
        els.gateSubmit.disabled = true;
        els.gateStatus.className = 'status';
        els.gateStatus.textContent = t('loading');
        try {
            const res = await api('/access', { method: 'POST', body: JSON.stringify({ name }) });
            setToken(res.token);
            if (!getName()) setName(name);
            // Reset the class too, not just the text — a previous failed
            // attempt leaves the error styling behind.
            els.gateStatus.className = 'status';
            els.gateStatus.textContent = '';
            await load();
        } catch (err) {
            els.gateStatus.className = 'status error';
            els.gateStatus.textContent = err.status === 403 ? t('gateFailed') : (err.message || t('gateFailed'));
        } finally {
            els.gateSubmit.disabled = false;
        }
    }

    // ------------------------------------------------------- image pipeline
    /**
     * Decode a file and produce the three renditions the server expects.
     * createImageBitmap handles EXIF orientation natively where supported,
     * which matters: without it every portrait iPhone photo lands sideways.
     */
    async function makeRenditions(file) {
        // The picker accepts HEIC, but only Safari can decode it; every other
        // browser gets a JPEG from utils/heic-image.js first.
        if (window.qrHeic) file = await window.qrHeic.toDecodableImage(file);
        let bitmap;
        try {
            bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
            // Safari < 17 and some Android browsers: fall back to an <img>.
            bitmap = await new Promise((resolve, reject) => {
                const url = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
                img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
                img.src = url;
            });
        }

        const srcW = bitmap.width || bitmap.naturalWidth;
        const srcH = bitmap.height || bitmap.naturalHeight;
        if (!srcW || !srcH) throw new Error('decode failed');

        const out = {};
        for (const key of Object.keys(RENDITIONS)) {
            const cfg = RENDITIONS[key];
            const scale = Math.min(1, cfg.max / Math.max(srcW, srcH));
            const w = Math.max(1, Math.round(srcW * scale));
            const h = Math.max(1, Math.round(srcH * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(bitmap, 0, 0, w, h);
            // Always JPEG: one content type for all three renditions keeps the
            // server's magic-byte check and extension handling simple, and HEIC
            // is normalised away here rather than failing server-side.
            out[key] = canvas.toDataURL('image/jpeg', cfg.quality);
            canvas.width = canvas.height = 0; // release on mobile Safari
        }
        if (bitmap.close) bitmap.close();
        // The gallery lays out justified rows, which needs each photo's shape
        // before its image loads. Report the SOURCE dimensions (after EXIF
        // orientation, which createImageBitmap has already applied).
        out.width = srcW;
        out.height = srcH;
        return out;
    }

    // ------------------------------------------------------------- uploading
    els.uploadBtn.addEventListener('click', () => {
        if (busy) return;
        if (!getName()) { showNamePrompt(); return; }
        els.file.click();
    });

    function showNamePrompt() {
        els.guestName.value = getName();
        els.namePrompt.classList.add('open');
        setTimeout(() => els.guestName.focus(), 50);
    }
    els.nameCancel.addEventListener('click', () => els.namePrompt.classList.remove('open'));
    els.nameSubmit.addEventListener('click', confirmName);
    els.guestName.addEventListener('keydown', (e) => { if (e.key === 'Enter') confirmName(); });

    function confirmName() {
        const v = els.guestName.value.trim();
        if (v.length < 2) return;
        setName(v);
        els.namePrompt.classList.remove('open');
        els.file.click();
    }

    els.file.addEventListener('change', async () => {
        const files = Array.from(els.file.files || []);
        els.file.value = '';
        if (!files.length) return;
        await uploadAll(files);
    });

    /** True only for albums/demo-guest-album — see load(). */
    let isDemo = false;

    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    /** Clip length from the file's own metadata, or null if the browser cannot read it. */
    function videoDuration(file) {
        return new Promise((resolve) => {
            const el = document.createElement('video');
            el.preload = 'metadata';
            const url = URL.createObjectURL(file);
            const finish = (d) => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(d); };
            const timer = setTimeout(() => finish(null), 8000);
            el.onloadedmetadata = () => finish(Number.isFinite(el.duration) ? el.duration : null);
            el.onerror = () => finish(null);
            el.src = url;
        });
    }

    /**
     * Resumable (tus 1.0) upload straight to Cloudflare Stream. Chunks of
     * 50 MiB (Stream wants multiples of 256 KiB, at least 5 MiB); a failed
     * chunk re-reads the server's offset and resumes there, so a dropped
     * phone connection costs one chunk, not the whole clip.
     */
    async function tusUpload(url, file, onPct) {
        const CHUNK = 50 * 1024 * 1024;
        const head = async () => {
            const r = await fetch(url, { method: 'HEAD', headers: { 'Tus-Resumable': '1.0.0' } });
            const o = Number(r.headers.get('Upload-Offset'));
            return Number.isFinite(o) ? o : null;
        };
        let offset = 0;
        while (offset < file.size) {
            const end = Math.min(offset + CHUNK, file.size);
            let advanced = false;
            for (let attempt = 0; attempt < 4 && !advanced; attempt++) {
                try {
                    const r = await fetch(url, {
                        method: 'PATCH',
                        headers: {
                            'Tus-Resumable': '1.0.0',
                            'Upload-Offset': String(offset),
                            'Content-Type': 'application/offset+octet-stream'
                        },
                        body: file.slice(offset, end)
                    });
                    if (r.ok) {
                        const next = Number(r.headers.get('Upload-Offset'));
                        offset = Number.isFinite(next) && next > offset ? next : end;
                        advanced = true;
                        break;
                    }
                    if (r.status >= 400 && r.status < 500 && r.status !== 409 && r.status !== 423) {
                        throw Object.assign(new Error('upload refused'), { fatal: true });
                    }
                } catch (err) {
                    if (err && err.fatal) throw err;
                }
                await sleep(1500 * (attempt + 1));
                try { const o = await head(); if (o !== null) offset = o; } catch { /* retry blind */ }
            }
            if (!advanced) throw new Error('upload failed');
            onPct(Math.min(100, Math.round((offset / file.size) * 100)));
        }
    }

    /** One video after another; stops the batch on a limit, like photos. */
    async function uploadVideos(videos, v) {
        const max = (album && album.videoMaxSeconds) || 60;
        const trackingId = new URLSearchParams(location.search).get('tid') || null;
        for (let i = 0; i < videos.length; i++) {
            const file = videos[i];
            setStatus(t('videoUploading', { done: i + 1, total: videos.length, pct: 0 }));
            const dur = await videoDuration(file);
            if (dur !== null && dur > max + 0.5) { v.tooLong++; continue; }
            let started;
            try {
                started = await api('/videos', {
                    method: 'POST',
                    body: JSON.stringify({ uploaderName: getName(), bytes: file.size, trackingId })
                });
            } catch (err) {
                if (err && err.code === 'GUEST_VIDEO_LIMIT') { v.cap = err.limit || 20; return; }
                if (err && err.code === 'ALBUM_VIDEO_LIMIT') { v.albumFull = true; return; }
                if (err && err.code === 'VIDEO_NOT_AVAILABLE') { v.notAccepted += videos.length - i; return; }
                if (err && err.status === 429) { v.slowed = videos.length - i; return; }
                v.failed++;
                continue;
            }
            try {
                await tusUpload(started.uploadUrl, file, (pct) =>
                    setStatus(t('videoUploading', { done: i + 1, total: videos.length, pct })));
            } catch (err) {
                console.warn('[album] video upload failed:', err && err.message);
                v.failed++;
                continue;
            }
            // Stream encodes after the last byte; ask until it is ready, then
            // leave the rest to the server (the next page load settles it).
            let status = 'processing';
            for (let k = 0; k < 20 && status === 'processing'; k++) {
                await sleep(k === 0 ? 1500 : 4000);
                try {
                    const r = await api('/videos/' + encodeURIComponent(started.id) + '/done', { method: 'POST' });
                    status = r.status || 'processing';
                } catch { /* keep polling */ }
            }
            if (status === 'live' || status === 'queued') v.added++;
            else if (status === 'failed') { if (dur === null) v.tooLong++; else v.failed++; }
            else v.processing++;
        }
    }

    async function uploadAll(files) {
        busy = true;
        els.uploadBtn.disabled = true;

        const isVideo = (f) => /^video\//.test(f.type || '') || /\.(mov|mp4|m4v|webm|3gp)$/i.test(f.name || '');
        const videos = files.filter(isVideo);
        const photos = files.filter((f) => !isVideo(f));
        const tooBig = photos.filter((f) => f.size > MAX_SOURCE_BYTES);
        const queue = photos.filter((f) => f.size <= MAX_SOURCE_BYTES);

        let done = 0;
        let ok = 0;
        let failed = 0;
        let pending = false;
        // A 429 refuses every file after it too, so the batch stops there:
        // `capLimit` = this guest reached the per-guest photo limit (for good),
        // `slowed` = the upload rate limit (for a minute). `notSent` counts the
        // files never tried. Before this the loop kept resizing and posting
        // each one, and the guest read "N could not be added. Please try
        // again", so they did — 53 refusals in 35 minutes (2026-09-29).
        let capLimit = 0;
        let slowed = false;
        let notSent = 0;

        for (const file of queue) {
            setStatus(t('uploading', { done: done + 1, total: queue.length }));
            try {
                const renditions = await makeRenditions(file);
                const payload = JSON.stringify({
                    full: renditions.full,
                    display: renditions.display,
                    thumb: renditions.thumb,
                    contentType: 'image/jpeg',
                    uploaderName: getName(),
                    width: renditions.width,
                    height: renditions.height,
                    trackingId: new URLSearchParams(location.search).get('tid') || null
                });

                if (isDemo) {
                    // Same renditions a real upload makes, then straight into
                    // the grid. No request, nothing stored, nothing to clean up
                    // — a refresh clears it, which is the honest behaviour to
                    // show for a promise that the photo never leaves the phone.
                    currentPhotos = [{
                        id: 'demo-local-' + done,
                        thumbUrl: renditions.thumb,
                        displayUrl: renditions.display,
                        fullUrl: renditions.display,
                        uploaderName: getName(),
                        width: renditions.width,
                        height: renditions.height
                    }].concat(currentPhotos);
                    render(currentPhotos);
                    ok++;
                    done++;
                    continue;
                }

                let res = null;
                for (let attempt = 0; attempt < 2 && !res; attempt++) {
                    try {
                        res = await api('/photos', { method: 'POST', body: payload });
                    } catch (err) {
                        // A 4xx is a real refusal (too large, cap reached, bad
                        // name) — retrying it just wastes the guest's time.
                        // Only a network hiccup or a 5xx is worth a second go.
                        if (err.status && err.status < 500) throw err;
                        if (attempt === 1) throw err;
                    }
                }
                ok++;
                if (res && res.photo && res.photo.pending) pending = true;
            } catch (err) {
                if (err && err.status === 429) {
                    if (err.code === 'GUEST_PHOTO_LIMIT') capLimit = err.limit || 200;
                    else slowed = true;
                    notSent = queue.length - done;
                    break;
                }
                failed++;
                console.warn('[album] upload failed:', err && err.message);
            }
            done++;
        }

        const v = { added: 0, processing: 0, tooLong: 0, failed: 0, notAccepted: 0, cap: 0, albumFull: false };
        if (videos.length && !capLimit && !slowed) {
            if (!album || album.acceptsVideo !== true || isDemo) {
                v.notAccepted = videos.length;
            } else {
                await uploadVideos(videos, v);
                if (v.slowed) { slowed = true; notSent += v.slowed; }
            }
        }

        busy = false;
        els.uploadBtn.disabled = false;

        const notes = [];
        if (ok) notes.push(t('uploaded', { n: ok }));
        if (v.added) notes.push(t('videosAdded', { n: v.added }));
        if (v.processing) notes.push(t('videoProcessing'));
        if (v.tooLong) notes.push(t('videoTooLong', { n: v.tooLong, max: (album && album.videoMaxSeconds) || 60 }));
        if (v.failed) notes.push(t('videoFailed', { n: v.failed }));
        if (v.cap) notes.push(t('videoCap', { n: v.cap }));
        if (v.albumFull) notes.push(t('albumVideoFull'));
        if (v.notAccepted) notes.push(t('videosNotAccepted', { n: v.notAccepted }));
        if (pending) notes.push(t('pendingNote'));
        if (failed) notes.push(t('someFailed', { n: failed }));
        if (capLimit) notes.push(t('capReached', { n: capLimit }));
        if (slowed) notes.push(t('slowDown', { n: notSent }));
        if (tooBig.length) notes.push(t('tooBig', { n: tooBig.length }));
        // Restate the demo promise right where the confirmation lands: this is
        // the moment someone might reasonably assume the photo was sent.
        if (isDemo && ok) notes.push(t('demoNote'));
        const isError = failed || slowed || tooBig.length || v.failed || v.tooLong || v.notAccepted;
        setStatus(notes.join(' '), isError ? 'error' : 'ok');

        // Repaint from the server so the guest sees their photo in the grid —
        // that confirmation is the whole emotional payoff of the feature. The
        // demo has already painted locally, and reloading would wipe it.
        if (!isDemo) await load();
        if (ok || capLimit || slowed || v.added || v.processing || v.failed || v.tooLong || v.cap || v.albumFull || v.notAccepted) setStatus(notes.join(' '), isError ? 'error' : 'ok');
    }

    // ------------------------------------------------------------------ boot
    applyStaticStrings();
    if (!slug || slug.length < 6) {
        setStatus(t('notFound'), 'error');
    } else {
        load();
    }
})();
